/**
 * @file        src/lib/fareTypes.js
 * @description The TripJack fare families a flight markup can be limited to,
 *              labelled the way the STOREFRONT prints them on the fare popup
 *              (PUBLISHED / LITE / SALE / NDC XPRESS VALUE …) so the name an
 *              admin picks is the name a customer sees. The value is the
 *              supplier's own fareIdentifier; booking_backend's marginRuleService
 *              compares it case-, space- and underscore-insensitively, so
 *              "NDC_Xpress Value" and "NDC_XPRESS_VALUE" are the same rule.
 *              Shared by Neon and Beyond — one console serves both brands.
 * @author      Kinetika Labs
 * @created     2026-10-05
 * @changelog
 *   - 2026-10-05 Claude — Initial. Replaces MarkupList's hand-typed list, which
 *                          used names the storefront never prints and two values
 *                          (`COUPON`, and `OFFER` for the PNR variant) that no
 *                          live fare could match.
 *   - 2026-10-05 Claude — flightScopeFields() / flightScopeError(): the Airline
 *                          Type / Airline Code / Fare Type trio the Flight Markup
 *                          screen has, now shared by the Coupon and Convenience
 *                          Fee screens (admin migration 074).
 *   - 2026-10-08 Claude — Removed GP_PRO / GP_ELITE: no fare this platform sells
 *                          carries them (nothing in the storefronts or backend
 *                          names either), so a rule scoped to one never matched.
 *   - 2026-10-08 Claude — Removed ARMED_FORCES (the storefronts search only
 *                          Regular / Student / Senior Citizen, so no fare can
 *                          carry it). Added the families TripJack returns most
 *                          that were missing: Upfront, Flex, Promo, Premium
 *                          Value / Flex, Business Value / Flex. Matching is
 *                          fareFamilyKey's, so "BUSINESS FLEX" = BUSINESS_FLEX.
 */

// Ordered roughly by how often a search returns them. A label in brackets is
// the supplier family behind a storefront name that differs from it.
export const FLIGHT_FARE_TYPES = [
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'SALE', label: 'Sale' },
  { value: 'LITE', label: 'Lite' },
  { value: 'VALUE', label: 'Value' },
  { value: 'FLEXI', label: 'Flexi' },
  { value: 'FLEXI_PLUS', label: 'Flexi Plus' },
  { value: 'STRETCH', label: 'Stretch' },
  { value: 'STRETCH_PLUS', label: 'Stretch Plus' },
  { value: 'FAMILY', label: 'Family' },
  { value: 'TACTICAL', label: 'Saver (Tactical)' },
  { value: 'SME', label: 'SME' },
  { value: 'CORPORATE', label: 'Corporate' },
  { value: 'COUPONFARE', label: 'Coupon' },
  { value: 'OFFER', label: 'Exclusive Fare (Offer)' },
  { value: 'SPECIAL_RETURN', label: 'Special Return' },
  { value: 'ECO_VALUE', label: 'Eco Value' },
  { value: 'ECO_CLASSIC', label: 'Eco Classic' },
  { value: 'ECO_FLEX', label: 'Eco Flex' },
  { value: 'NDC_VALUE', label: 'NDC Value' },
  { value: 'NDC_XPRESS_VALUE', label: 'NDC Xpress Value' },
  { value: 'NDC_CORPORATE_VALUE', label: 'NDC Corporate Value' },
  { value: 'NDC_CORPORATE_FLEX', label: 'NDC Corporate Flex' },
  { value: 'SENIOR_CITIZEN', label: 'Senior Citizen' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'UPFRONT', label: 'Upfront' },
  { value: 'FLEX', label: 'Flex' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'PREMIUM_VALUE', label: 'Premium Value' },
  { value: 'PREMIUM_FLEX', label: 'Premium Flex' },
  { value: 'BUSINESS_VALUE', label: 'Business Value' },
  { value: 'BUSINESS_FLEX', label: 'Business Flex' },
];

// ALL = every airline, Airline Wise = the one in Airline Code — the Flight
// Markup screen's "Markup Type", stored as the discount tables' airline_type.
export const AIRLINE_TYPE = [
  { value: 'all', label: 'ALL' },
  { value: 'airline_wise', label: 'Airline Wise' },
];

/**
 * The Airline Type / Airline Code / Fare Type fields for a SimpleLookupPage
 * flight rule (Coupon, Convenience Fee). Code and fare type are `nullable` so
 * blanking one on edit really clears it, rather than keeping the stored value.
 */
export function flightScopeFields() {
  return [
    { key: 'airline_type', label: 'Airline Type', type: 'select', default: 'all', options: AIRLINE_TYPE },
    { key: 'airline_code', label: 'Airline Code', type: 'text', maxLength: 10, nullable: true, placeholder: 'e.g. 6E / AI (Airline Wise only)' },
    { key: 'fare_type', label: 'Fare Type', type: 'select', options: FLIGHT_FARE_TYPES, allowOther: true, nullable: true, maxLength: 30, otherPlaceholder: 'Fare type as shown on the site, e.g. Xpress Lite' },
  ];
}

/** Field errors for the trio — an Airline Wise rule needs its airline. */
export function flightScopeError(form) {
  if (form.airline_type === 'airline_wise' && !String(form.airline_code || '').trim()) {
    return { airline_code: 'Enter the airline code for an Airline Wise rule.' };
  }
  return {};
}
